import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { PurchaseOrderDetail as PurchaseOrderData, PurchaseOrderItem } from '@/lib/api';
import { ArrowLeft, ChevronDown, ChevronRight, ClipboardList, Loader2, Package, Save, CheckCircle, Archive } from 'lucide-react';
import clsx from 'clsx';

function formatDate(dateStr: string | null) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className={clsx(
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-bold',
        status === 'DRAFT' && 'bg-risk-yellow-soft text-risk-yellow',
        status === 'ORDERED' && 'bg-brand-soft text-brand-deep',
        status === 'ARCHIVED' && 'bg-surface-2 text-ink-soft'
      )}
    >
      {status === 'DRAFT' ? 'Entwurf' : status === 'ORDERED' ? 'Bestellt' : 'Archiviert'}
    </span>
  );
}

interface SupplierGroup {
  supplierName: string;
  supplierId: string | null;
  items: PurchaseOrderItem[];
}

function groupBySupplier(items: PurchaseOrderItem[]): SupplierGroup[] {
  const groups = new Map<string, SupplierGroup>();

  for (const item of items) {
    const key = item.supplierId ?? '__none__';
    if (!groups.has(key)) {
      groups.set(key, {
        supplierName: item.supplierName ?? 'Kein Lieferant',
        supplierId: item.supplierId,
        items: [],
      });
    }
    groups.get(key)!.items.push(item);
  }

  // Sort: named suppliers first, then "Kein Lieferant"
  const result = Array.from(groups.values());
  result.sort((a, b) => {
    if (a.supplierId === null && b.supplierId !== null) return 1;
    if (a.supplierId !== null && b.supplierId === null) return -1;
    return a.supplierName.localeCompare(b.supplierName);
  });

  return result;
}

export default function PurchaseOrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<PurchaseOrderData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [statusChanging, setStatusChanging] = useState(false);
  const [message, setMessage] = useState('');
  const [editedQtys, setEditedQtys] = useState<Record<string, number>>({});
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  const loadData = async () => {
    if (!id) return;
    try {
      const data = await api.getPurchaseOrder(id);
      setOrder(data);
      // Initialize edited qtys
      const qtys: Record<string, number> = {};
      data.items.forEach((item) => {
        qtys[item.sku] = item.orderedQty;
      });
      setEditedQtys(qtys);
    } catch (err) {
      console.error('Fehler beim Laden der Bestellliste:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [id]);

  const toggleGroup = (key: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const handleSave = async () => {
    if (!order || !id) return;
    setSaving(true);
    setMessage('');
    try {
      // Save each changed qty
      for (const item of order.items) {
        const newQty = editedQtys[item.sku];
        if (newQty !== undefined && newQty !== item.orderedQty) {
          await api.updatePurchaseOrderItemQty(id, item.sku, newQty);
        }
      }
      await loadData();
      setMessage('Bestellliste gespeichert.');
    } catch (err) {
      console.error('Speichern fehlgeschlagen:', err);
      setMessage('Fehler beim Speichern.');
    } finally {
      setSaving(false);
    }
  };

  const handleMarkAsOrdered = async () => {
    if (!id) return;
    if (!confirm('Hiermit markierst du diese Bestellliste als bestellt. Mengen können danach nicht mehr geändert werden.')) return;
    setStatusChanging(true);
    try {
      await api.updatePurchaseOrderStatus(id, 'markAsOrdered');
      await loadData();
    } catch (err) {
      console.error('Statusänderung fehlgeschlagen:', err);
    } finally {
      setStatusChanging(false);
    }
  };

  const handleArchive = async () => {
    if (!id) return;
    if (!confirm('Archivierte Listen können nicht mehr bearbeitet werden.')) return;
    setStatusChanging(true);
    try {
      await api.updatePurchaseOrderStatus(id, 'archive');
      await loadData();
    } catch (err) {
      console.error('Archivierung fehlgeschlagen:', err);
    } finally {
      setStatusChanging(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-brand" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="text-center py-12 text-ink-muted">
        Bestellliste nicht gefunden.
      </div>
    );
  }

  const isDraft = order.status === 'DRAFT';
  const supplierGroups = groupBySupplier(order.items);
  const totalItems = order.items.length;
  const hasChanges = order.items.some((item) => editedQtys[item.sku] !== item.orderedQty);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <button
            onClick={() => navigate('/bestelllisten')}
            className="text-xs font-semibold text-ink-muted hover:text-ink flex items-center gap-1 mb-3 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Zurück zur Übersicht
          </button>
          <div className="flex items-center gap-3">
            <ClipboardList className="h-8 w-8 text-brand" />
            <div>
              <h1 className="text-2xl font-extrabold tracking-tight text-ink">
                Bestellliste vom {formatDate(order.createdAt)}
              </h1>
              <div className="flex items-center gap-3 mt-1.5">
                <StatusBadge status={order.status} />
                <span className="font-mono text-[11px] text-ink-soft">Erstellt von: {order.generatedByName}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {isDraft && (
            <button
              onClick={handleMarkAsOrdered}
              disabled={statusChanging}
              className="btn-primary flex items-center gap-2"
            >
              {statusChanging ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
              Als bestellt markieren
            </button>
          )}
          {(order.status === 'DRAFT' || order.status === 'ORDERED') && (
            <button
              onClick={handleArchive}
              disabled={statusChanging}
              className="btn-secondary flex items-center gap-2"
            >
              {statusChanging ? <Loader2 className="h-4 w-4 animate-spin" /> : <Archive className="h-4 w-4" />}
              Archivieren
            </button>
          )}
        </div>
      </div>

      {message && (
        <div className={clsx(
          'text-[12.5px] rounded-panel px-4 py-2.5 border',
          message.startsWith('Fehler')
            ? 'bg-risk-red-soft text-risk-red border-risk-red/20'
            : 'bg-brand-soft text-brand-deep border-brand-soft-border'
        )}>
          {message}
        </div>
      )}

      {/* Supplier Groups */}
      {supplierGroups.map((group) => {
        const groupKey = group.supplierId ?? '__none__';
        const isCollapsed = collapsedGroups.has(groupKey);
        const groupTotal = group.items.reduce((sum, item) => sum + (editedQtys[item.sku] ?? item.orderedQty), 0);

        return (
          <div key={groupKey} className="rounded-card border border-line bg-surface overflow-hidden">
            <button
              onClick={() => toggleGroup(groupKey)}
              className="w-full flex items-center justify-between px-5 py-4 border-b border-line hover:bg-surface-2 transition-colors"
            >
              <div className="flex items-center gap-2">
                {isCollapsed ? (
                  <ChevronRight className="h-4 w-4 text-ink-soft" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-ink-soft" />
                )}
                <Package className="h-4 w-4 text-brand" />
                <span className="text-[16.5px] font-extrabold tracking-tight text-ink">
                  {group.supplierName}
                </span>
                <span className="font-mono text-[11px] text-ink-soft">
                  ({group.items.length} Artikel)
                </span>
              </div>
              <span className="font-mono text-[12.5px] text-ink-muted">
                Gesamt: {groupTotal.toLocaleString('de-DE')} Stk.
              </span>
            </button>

            {!isCollapsed && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-surface-3 text-left">
                      <th className="px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-ink-soft">SKU</th>
                      <th className="px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-ink-soft">Produkt</th>
                      <th className="px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-ink-soft text-right">OOS-Datum</th>
                      <th className="px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-ink-soft text-right">Reichweite</th>
                      <th className="px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-ink-soft text-right">Bestand</th>
                      <th className="px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-ink-soft text-right">Tägl. Verbrauch</th>
                      <th className="px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-ink-soft text-right">Empf. Menge</th>
                      <th className="px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-ink-soft text-right">Bestellmenge</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.items.map((item) => (
                      <tr key={item.sku} className="border-b border-surface-3 hover:bg-surface-2 transition-colors">
                        <td className="px-4 py-3 font-mono text-[10.5px] text-ink-soft">{item.sku}</td>
                        <td className="px-4 py-3 text-[13.5px] font-bold text-ink">{item.productName}</td>
                        <td className="px-4 py-3 font-mono text-[12.5px] text-right">{formatDate(item.oosDate)}</td>
                        <td className="px-4 py-3 font-mono text-[12.5px] text-right">
                          {item.coverageDays !== null ? `${item.coverageDays} Tage` : '—'}
                        </td>
                        <td className="px-4 py-3 font-mono text-[12.5px] text-right">
                          {item.onHand !== null ? item.onHand.toLocaleString('de-DE') : '—'}
                        </td>
                        <td className="px-4 py-3 font-mono text-[12.5px] text-right">
                          {item.dailyUsage !== null ? Number(item.dailyUsage).toFixed(2) : '—'}
                        </td>
                        <td className="px-4 py-3 font-mono text-[12.5px] text-right">{item.recommendedQty.toLocaleString('de-DE')}</td>
                        <td className="px-4 py-3 text-right">
                          {isDraft ? (
                            <input
                              type="number"
                              min={0}
                              value={editedQtys[item.sku] ?? item.orderedQty}
                              onChange={(e) =>
                                setEditedQtys((prev) => ({
                                  ...prev,
                                  [item.sku]: parseInt(e.target.value, 10) || 0,
                                }))
                              }
                              className="w-24 text-right input-field font-mono text-[12.5px] py-1 px-2"
                            />
                          ) : (
                            <span className="font-mono text-[12.5px] text-ink">
                              {(editedQtys[item.sku] ?? item.orderedQty).toLocaleString('de-DE')}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}

      {/* Footer */}
      <div className="flex items-center justify-between">
        <span className="font-mono text-[11px] text-ink-soft">
          {totalItems} {totalItems === 1 ? 'Artikel' : 'Artikel'} insgesamt
        </span>
        {isDraft && (
          <button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className={clsx(
              'btn-primary flex items-center gap-2',
              !hasChanges && 'opacity-50 cursor-not-allowed'
            )}
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Speichern
          </button>
        )}
      </div>
    </div>
  );
}
